/* useProgressionLiveActivity.js — Fetches the latest meaningful
 * operational activity per active deal in the workspace so the
 * mobile Sales Progression cards can surface real chain movement
 * instead of deterministic placeholders.
 *
 * Single source. Backed by GET /api/progression/live-activity.
 * Light in-memory cache so the hook can be called from multiple
 * surfaces (mobile progression, future desktop micro-rail, etc.)
 * without thrashing the backend.
 */
import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";

// Refresh once every 60s on visible surfaces — calm operational
// cadence, not a chat feed.
const REFRESH_MS = 60_000;

let _cache = null;
let _cacheAt = 0;
let _inflight = null;

async function fetchOnce(force = false) {
  const now = Date.now();
  if (!force && _cache && now - _cacheAt < REFRESH_MS) return _cache;
  if (_inflight) return _inflight;
  _inflight = (async () => {
    try {
      const res = await api.get("/api/progression/live-activity");
      const deals = res?.data?.deals || {};
      _cache = deals;
      _cacheAt = Date.now();
      return deals;
    } catch {
      // Calm degradation — the card falls back to its deterministic
      // placeholder if the endpoint is unavailable.
      _cache = _cache || {};
      _cacheAt = Date.now();
      return _cache;
    } finally {
      _inflight = null;
    }
  })();
  return _inflight;
}

export function useProgressionLiveActivity() {
  const [byDeal, setByDeal] = useState(_cache || {});
  const [loading, setLoading] = useState(!_cache);

  const refresh = useCallback(async (force = false) => {
    const data = await fetchOnce(force);
    setByDeal(data);
    setLoading(false);
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      const data = await fetchOnce(false);
      if (alive) {
        setByDeal(data);
        setLoading(false);
      }
    })();
    const t = setInterval(() => {
      fetchOnce(false).then((d) => alive && setByDeal(d));
    }, REFRESH_MS);
    return () => { alive = false; clearInterval(t); };
  }, []);

  return { byDeal, loading, refresh };
}

/* Human-friendly relative time for the calm operational card.
 * Examples: "Just now", "12m ago", "1h ago", "Today", "Yesterday",
 *           "2d ago", "Mon 10 Feb".
 */
export function formatRelativeOperational(iso) {
  if (!iso) return "";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "";
  const now = Date.now();
  const diffMs = now - t;
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1)  return "Just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 6)   return `${diffHr}h ago`;
  // Compare calendar days
  const d = new Date(t);
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  if (sameDay) return "Today";
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
  const diffDays = Math.floor((now - t) / 86_400_000);
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
}
